/**
 * What kind of business a workspace is — the axis the /insights headline row
 * switches on. Stored per workspace on `brand_kits.business_model` and inferred
 * by `infer_brand_kit_business_model` (migration 20260926007000) from evidence
 * Mora already holds until a person confirms it.
 *
 * Deliberately small and closed. A value earns its place only when it changes
 * which OUTCOME the page can measure: an order (commerce), a lead (lead-gen), a
 * booking/call (local service), or an audience (personal authority, creator).
 * Tier — Consumer/Franchise/Enterprise — is a different axis (`tiers.ts`).
 */
export const BUSINESS_MODELS = [
  'commerce',
  'lead_gen_b2b',
  'local_service',
  'personal_authority',
  'creator_media',
] as const;

export type BusinessModel = (typeof BUSINESS_MODELS)[number];

export const BUSINESS_MODEL_LABELS: Record<BusinessModel, string> = {
  commerce: 'Sells products online',
  lead_gen_b2b: 'Generates leads (B2B or services)',
  local_service: 'Local service business',
  personal_authority: 'Personal brand',
  creator_media: 'Creator or media',
};

export const BUSINESS_MODEL_DESCRIPTIONS: Record<BusinessModel, string> = {
  commerce: 'Insights opens on sales, ad spend, efficiency and orders.',
  lead_gen_b2b: 'Insights opens on ad spend, leads, cost per lead and landing page clicks.',
  local_service:
    'Insights opens on ad spend, leads, cost per lead and clicks. Calls and direction requests arrive with a Google Business Profile connection.',
  personal_authority: 'Insights opens on engagement across your channels, plus ad spend.',
  creator_media: 'Insights opens on engagement across your channels, plus ad spend.',
};

export function parseBusinessModel(value: unknown): BusinessModel | null {
  return typeof value === 'string' && (BUSINESS_MODELS as readonly string[]).includes(value)
    ? (value as BusinessModel)
    : null;
}

/**
 * Which outcome the headline row measures: an order, a lead, or an audience.
 * A workspace with no classification yet keeps today's commerce row — the
 * non-breaking default the inference also falls back to.
 */
export function headlineOutcome(model: BusinessModel | null): 'orders' | 'leads' | 'audience' {
  if (model === 'lead_gen_b2b' || model === 'local_service') return 'leads';
  if (model === 'personal_authority' || model === 'creator_media') return 'audience';
  return 'orders';
}
